import { styled } from "@mui/material/styles";

import { useImageLoader } from "@/hooks/useImageLoader";
import { Rectangle } from "@/types";
import { calculateTransformCSS } from "@/utils/images";

import { DottedBackground } from "../Backgrounds/Dotted";

const Root = styled(DottedBackground, {
  shouldForwardProp: (prop) => prop !== "aspectRatio",
})<{
  aspectRatio: number;
}>(({ aspectRatio }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  aspectRatio: `${aspectRatio} / 1`,
}));

const DarkenedOverlay = styled("div", {
  shouldForwardProp: (prop) => prop !== "clipPath",
})<{
  clipPath: string;
}>(({ clipPath }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.75)",
  clipPath,
}));

const PositionedImage = styled("img")({
  position: "absolute",
  width: "100%",
  height: "100%",
});

interface CroppedImagePreviewProps {
  cropWindow: Rectangle;
  src: string;
  alt?: string;
}

export const CroppedImagePreview = (props: CroppedImagePreviewProps) => {
  const { cropWindow, src, alt } = props;

  const { image: loadedImage } = useImageLoader(src);

  if (!loadedImage) {
    return null;
  }

  const aspectRatio = loadedImage.width / loadedImage.height;

  const { imageFrame, clipPath } = calculateTransformCSS(
    loadedImage,
    cropWindow,
    1,
  );

  return (
    <Root aspectRatio={aspectRatio}>
      <PositionedImage src={src} alt={alt} style={imageFrame} />
      <DarkenedOverlay clipPath={clipPath} />
    </Root>
  );
};
